<mat-dialog-content class="privacy-policy">
	<button class="o-modal-dismiss">
		<epi-icon
			mat-dialog-close
			use="close"
			size="24"
		></epi-icon>
	</button>
	<epi-heading
		level="2"
		appears-as="1"
		color="primary"
		class="privacy-policy__heading"
	>
		Embrace Pet Insurance Privacy Policy
	</epi-heading>
	<p class="privacy-policy__sub-header">
		<strong>Effective Date:</strong> 1/1/2024
	</p>
	<section
		id="privacy-policyTableOfContents"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content">
			Please click <epi-link
				to="https://assets.ctfassets.net/nx3pzsky0bc9/3i8GTIKhdkBfI9heqwelul/6334e163f8f19099fab2503a78232651/Embrace_Pet_Insurance_Privacy_Policy_01_01_2024.pdf"
				target="_blank"
			>here</epi-link> for a printable PDF version of this notice.
		</p>
		<p class="privacy-policy__content">
			This Privacy Policy describes how Embrace Pet Insurance (“Company” or “we”) collects, uses, shares and protects personal information when you interact with our website
			(<epi-link
				to="https://www.embracepetinsurance.com/"
				target="_blank"
			>www.embracepetinsurance.com</epi-link>), or mobile app, emails we send, social media accounts, and online services (our “Services”), including the following:
		</p>
		<ol class="privacy-policy__list u-margin-top-small">
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyWhatWeCollect">The Personal Information We Collect</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyCookiesAndAutomatedMeans">Online User Activity, Cookies and Information Collected by Other Automated Means</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyHowWeUseYourInformation">How We Use Your Personal Information</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyHowWeShareYourInformation">How We Share Your Personal Information</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyHowWeProtectYourInformation">How We Protect and Store Your Personal Information</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyYourPrivacyOptions">Your Privacy Options</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyChildrensInformation">Children's Information</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyUpdatesTo">Updates to this Privacy Policy</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyContact">Contact</a>
			</li>
			<li class="privacy-policy__list-item">
				<a (click)="scroll($event)" href="#privacy-policyAdditionalNotices">Additional Notices</a>
				<ol class="privacy-policy__list-sublist">
					<li class="privacy-policy__list-item">
						<a (click)="scroll($event)" href="#privacy-policyCaliforniaNotices">California Residents</a>
					</li>
					<li class="privacy-policy__list-item">
						<a (click)="scroll($event)" href="#privacy-policyVirginiaColoradoConnecticutUtah">Virginia, Colorado, Connecticut, and Utah Residents</a>
					</li>
				</ol>
			</li>
		</ol>
		<p class="privacy-policy__content">
			Please note that this Privacy Policy does not apply to non-Embrace websites or services that state that they are offered under a different privacy policy.
			Please review the privacy policies on those websites and applications directly to understand their privacy practices.
		</p>
	</section>
	<section
		id="privacy-policyWhatWeCollect"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p
			id="WhatWeCollect"
			class="privacy-policy__content m--bold"
		>
			The Personal Information We Collect
		</p>
		<p class="privacy-policy__content">
			Depending on the nature of your interaction with Embrace, we may collect personal information as follows:
		</p>
		<ol
			class="privacy-policy__list u-margin-top-small"
			aria-labelledby="WhatWeCollect"
		>
			<li class="privacy-policy__list-item">
				<u>Directly from you</u>, when you provide information to us or interact with us:
				<ol class="privacy-policy__list-sublist">
					<li class="privacy-policy__list-item">
						Contact information and any other information you choose to include when you fill out forms on our websites or otherwise communicate with us via e-mail, mail, phone or other channels.
						We record telephone conversations with our customer service center for record-keeping, training, and quality assurance purposes.
					</li>
					<li class="privacy-policy__list-item">
						Account information, such as your MyEmbrace username and password, your name, mailing address, e-mail address, phone number, and service-related information.
					</li>
					<li class="privacy-policy__list-item">
						Survey information in response to questions we may send you through the Services, including for feedback and research purposes.
					</li>
					<li class="privacy-policy__list-item">
						Transactional information including payment information and payment history if you engage in transactions through the Services.
					</li>
				</ol>
			</li>
			<li class="privacy-policy__list-item">
				<u>Automatically from you</u>, when you utilize or interact with our Services:
				<ol class="privacy-policy__list-sublist">
					<li class="privacy-policy__list-item">
						Device information and online user activity when you utilize our technology platforms, as described in Section 2 below.
					</li>
				</ol>
			</li>
		</ol>
		<p class="privacy-policy__content">
			Some of this information may be collected by external parties on our behalf. For example, we use a payment processor when you engage in a
			transaction on our website; Embrace does not itself collect or store payment card information.
		</p>
	</section>
	<section
		id="privacy-policyCookiesAndAutomatedMeans"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p
			id="CookiesAndAutomatedMeans"
			class="privacy-policy__content m--bold"
		>
			Online User Activity, Cookies and Information Collected by Other Automated Means
		</p>
		<p class="privacy-policy__content">
			When you interact with the Services, certain information about your use of our Services is automatically collected. This includes:
		</p>
		<ol
			class="privacy-policy__list u-margin-top-small"
			aria-labelledby="CookiesAndAutomatedMeans"
		>
			<li class="privacy-policy__list-item">
				Usage Details about your interaction with our Services (such as the date, time, and length of visits, and specific pages or content
				accessed during the visits, search terms, frequency of the visits, referring website addresses)
			</li>
			<li class="privacy-policy__list-item">
				Device Information including the IP address and other details of a device that you use to connect with our Services
				(such as device type and unique device identifier, operating system, and browser type,)
			</li>
			<li class="privacy-policy__list-item">
				We collect information about your interaction with the Services, including the resources that you access, pages viewed,
				how much time you spent on a page, and how you reached our website.
				We also log the details of your visits to our website and information generated in the course of using our website,
				such as mouse movements, clicks, page visits, text entered, how long you spent on a page, and other details of your actions on our website.
			</li>
		</ol>
		<p class="privacy-policy__content">
			Much of this information is collected through cookies, web beacons, and other tracking technologies, which may be operated by our partners who assist us
			in collecting information about usage of the Services, serving ads, or providing other services to you.
			For example, we use Google Analytics to collect usage details. You can learn more about privacy and Google Analytics by visiting
			<epi-link
				to="https://support.google.com/analytics/?hl=en#topic=14090456"
				target="_blank"
			>https://support.google.com/analytics/?hl=en#topic=14090456</epi-link>, and opt out of collection of your data by using the Google Analytics Opt-out Browser Add-on, available at
			<epi-link
				to="https://tools.google.com/dlpage/gaoptout"
				target="_blank"
			>https://tools.google.com/dlpage/gaoptout</epi-link>. We may also use cookies set by other third-party services.
		</p>
		<p class="privacy-policy__content">
			You may be able to opt out of tracking by cookies or control how information collected by cookies is used via a number of means, as described in Section 7 below.
		</p>
		<p class="privacy-policy__content">
			Your browser or device may include “Do Not Track” functionality; please note that we do not respond to such signals.
		</p>
	</section>
	<section
		id="privacy-policyHowWeUseYourInformation"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p
			id="HowWeUseYourInformation"
			class="privacy-policy__content m--bold"
		>
			How We Use Your Personal Information
		</p>
		<p class="privacy-policy__content">
			We use the personal information we collect from you for the following purposes:
		</p>
		<ol
			class="privacy-policy__list u-margin-top-small"
			aria-labelledby="HowWeUseYourInformation"
		>
			<li class="privacy-policy__list-item">
				Provide our Services to you, including to maintain and service your account, manage your policy, process your claims, and provide notices and other important updates.
			</li>
			<li class="privacy-policy__list-item">
				Respond to your requests, questions and comments and provide customer support.
			</li>
			<li class="privacy-policy__list-item">
				Monitor the performance of our Services including metrics such as total number of visitors, traffic, and demographic patterns.
			</li>
			<li class="privacy-policy__list-item">
				Interact with you, including to notify you regarding products, services, and promotions that may be of interest to you
				and inform you about important changes to this Privacy Policy, our Terms or other policies.
			</li>
			<li class="privacy-policy__list-item">
				Tailor the content we display to you in our Services and communications, including advertising, and offer products and services that we believe may be of interest to you.
			</li>
			<li class="privacy-policy__list-item">
				Manage, operate, and improve the Services, including understanding our customer base
				and the effectiveness of our marketing, events, promotional campaigns, and publications, and diagnose or fix technology problems.
			</li>
			<li class="privacy-policy__list-item">
				Comply with legal requirements and industry standards, detect, investigate, and prevent activities that may violate our policies or be fraudulent or illegal,
				and protect the rights of Embrace, you, or others.
			</li>
		</ol>
		<p class="privacy-policy__content">
			We may use and share information in an aggregated or de-identified manner at our discretion, including for research, analysis, modeling, marketing, and improvement of our Services.
		</p>
	</section>
	<section
		id="privacy-policyHowWeShareYourInformation"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p
			id="HowWeShareYourInformation"
			class="privacy-policy__content m--bold"
		>
			How We Share Your Personal Information
		</p>
		<ol
			class="privacy-policy__list u-margin-top-small"
			aria-labelledby="HowWeShareYourInformation"
		>
			<li class="privacy-policy__list-item">
				<u>Service Providers</u>. We may share your information with service providers that we believe need the information to perform a technology, business, or other professional function for us
				such as, IT services, maintenance and hosting of our Services, marketing partners, accounting, auditing, and tax services, and other professional services.
			</li>
			<li class="privacy-policy__list-item">
				<u>Analytics Partners</u>. We partner with analytics providers, who collect information via tracking technologies on our websites
				to assist us with measuring visits and traffic on our websites so we can measure and improve the performance of the sites.
			</li>
			<li class="privacy-policy__list-item">
				<u>Advertising Partners</u>. We partner with third parties who assist us in serving advertising regarding the Services to others who may be interested in the Services.
				These third parties may use tracking technologies to collect or receive information from the Services and elsewhere on the internet and use that information to provide measurement services and target ads.
				Third parties may allow other companies to access information about you so that they may market other products you may be interested in.
			</li>
			<li class="privacy-policy__list-item">
				<u>Social Media Platforms</u>. If you interact with us on social media platforms, the platform may be able to collect information about you and your interaction with us.
				If you interact with social media objects on our Services (for example, by clicking on a Facebook “like” button), both the platform and your connections on the platform may be able to view that activity.
				To control this sharing of information, please review the privacy policy of the relevant social media platform.
			</li>
			<li class="privacy-policy__list-item">
				<u>Affiliates and Partners</u>. We share information with members of our corporate family, including subsidiaries and strategic partners, for purposes consistent with this Privacy Policy.
			</li>
			<li class="privacy-policy__list-item">
				<u>Corporate Event</u>. We reserve the right to transfer to another entity or its affiliates or service providers some or all information about you in connection with,
				or during negotiations of, any merger, acquisition, sale of assets or any line of business, change in ownership control, or financing transaction.
				We cannot promise that an acquiring party or the merged entity will have the same privacy practices or treat your information the same as described in this Policy.
			</li>
			<li class="privacy-policy__list-item">
				<u>Legal</u>. We share information where necessary to comply with applicable law, to respond to requests from law enforcement agencies or other government authorities or third parties,
				as permitted by law and without your consent when it is necessary to protect our customers, employees, or property; in emergency situations; or to enforce our rights under our terms of service and policies.
			</li>
		</ol>
	</section>
	<section
		id="privacy-policyHowWeProtectYourInformation"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			How We Protect and Store Your Personal Information
		</p>
		<p class="privacy-policy__content">
			We use a combination of physical, technical, and administrative safeguards to protect the information we collect through the Services.
			While we use these precautions to safeguard your information, we cannot guarantee the security of the networks, systems, servers, devices, and databases we operate or that are operated on our behalf.
		</p>
		<p class="privacy-policy__content">
			We will retain your personal information for as long as is necessary to complete the purposes for which it was collected, or as may be required by law.
		</p>
	</section>
	<section
		id="privacy-policyYourPrivacyOptions"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p
			id="YourPrivacyOptions"
			class="privacy-policy__content m--bold"
		>
			Your Privacy Options
		</p>
		<p class="privacy-policy__content">
			You may have certain choices when it comes to how we collect and use your personal information:
		</p>
		<ol
			class="privacy-policy__list u-margin-top-small"
			aria-labelledby="YourPrivacyOptions"
		>
			<li class="privacy-policy__list-item">
				<u>Account Information</u>. Please visit your customer account page
				<epi-link
					to="https://my.embracepetinsurance.com"
					target="_blank"
				>https://my.embracepetinsurance.com</epi-link> to update your personal information.

			</li>
			<li class="privacy-policy__list-item">
				<u>Email Marketing and Communications</u>. If at any time you no longer wish to receive marketing communications from us,
				you can click the unsubscribe link at the bottom of any email or email us at
				<epi-link
					to="mailto:compliance@embracepetinsurance.com"
					target="_blank"
				>compliance@embracepetinsurance.com</epi-link>
			</li>
			<li class="privacy-policy__list-item">
				<u>Online Activity Tracking and Interest-Based Advertising</u>. You have several options to either prevent our collection of information regarding your online activity,
				or prevent third parties from using such information from being used for interest-based advertisements:
				<ol class="privacy-policy__list-sublist">
					<li class="privacy-policy__list-item">
						You may also modify your browser settings to disable or reject cookies across the internet; but if you do so, some features of our website or Services may not function properly or be available.
						If you are visiting the Services from a mobile device, the operating system of the device may offer you options regarding how the device collects and uses your information for interest-based advertising.
						Please visit
						<epi-link
							to="https://thenai.org/opt-out/mobile-opt-out/"
							target="_blank"
						>https://thenai.org/opt-out/mobile-opt-out/</epi-link> for more information.
					</li>
					<li class="privacy-policy__list-item">
						You may be able to prevent third parties from using your information for interest-based advertisements across the internet by visiting
						<epi-link
							to="https://thenai.org/"
							target="_blank"
						>https://thenai.org/</epi-link> or
						<epi-link
							to="https://youradchoices.com/"
							target="_blank"
						>https://youradchoices.com/</epi-link>. Please note this does not opt you out of being served ads,
						nor will it prevent the receipt of interest-based advertising from other companies that do not participate in these programs.
					</li>
				</ol>
			</li>
		</ol>
	</section>
	<section
		id="privacy-policyChildrensInformation"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			Children's Information
		</p>
		<p class="privacy-policy__content">
			We do not knowingly or intentionally gather Personal information about children.
			If we have inadvertently collected the Personal Information of a child under 16, or equivalent minimum age depending on jurisdiction,
			a parent or guardian of that child may contact us at
			<epi-link
				to="mailto:compliance@embracepetinsurance.com"
				target="_blank"
			>compliance@embracepetinsurance.com</epi-link>
			to request that we delete the information from our records or otherwise cease the use of that information.
		</p>
	</section>
	<section
		id="privacy-policyUpdatesTo"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			Updates to This Privacy Policy
		</p>
		<p class="privacy-policy__content">
			We may make changes to this Privacy Policy from time to time. The “Effective Date” at the top of this page indicates when this Privacy Policy was last revised.
			We may also notify you in other ways from time to time about the collection, use, and disclosure of your personal information described in this Privacy Policy.
		</p>
	</section>
	<section
		id="privacy-policyContact"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			Contact
		</p>
		<p class="privacy-policy__content">
			For questions about our privacy practices, contact us at
			<epi-link
				to="mailto:compliance@embracepetinsurance.com"
				target="_blank"
			>compliance@embracepetinsurance.com</epi-link> or write to us at:
		</p>
		<ul>
			<li>Embrace Pet Insurance</li>
			<li>P.O. Box 22188 </li>
			<li>Beachwood, OH 44122-0188 </li>
		</ul>
	</section>
	<section
		id="privacy-policyAdditionalNotices"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			Additional Notices
		</p>
		<section id="privacy-policyCaliforniaNotices">
			<p class="privacy-policy__content m--bold">
				CALIFORNIA RESIDENTS
			</p>
			<p
				id="CaliforniaNotices"
				class="privacy-policy__content m--bold"
			>
				Your California Privacy Rights
			</p>
			<p class="privacy-policy__content">
				California's "Shine the Light" law permits customers in California to request certain details about how certain types of their information are shared with third parties for those third parties' own direct marketing purposes.
				If you are a California resident, you may opt-out of such sharing by emailing us at
				<epi-link
					to="mailto:compliance@embracepetinsurance.com"
					target="_blank"
				>compliance@embracepetinsurance.com</epi-link> or mailing a written request to the address above.
			</p>
			<p class="privacy-policy__content m--bold">
				California Consumer Privacy Act
			</p>
			<p class="privacy-policy__content">
				California law provides California residents with rights to receive certain disclosures regarding the collection, use, and sharing of “Personal Information,”
				rights to access, delete, correct certain Personal Information we collect about them, restrict us from “selling” or “sharing” certain Personal Information,
				and limit our use of Sensitive Personal Information, as defined by the law and described in the categories below.
				These rights apply to all residents of CA, regardless of whether you are a customer, website visitor, business contact, or member of the workforce.
				As a California resident, you have a right not to receive discriminatory treatment for the exercise of your privacy rights.
			</p>
			<p class="privacy-policy__content">
				If you are a customer, website visitor, or business contact, please review this notice to learn more about your rights.
				If you are a candidate for employment, please see our California Notice of Collection for Job Candidates.
			</p>
			<p class="privacy-policy__content">
				<u>Collection, Use, and Retention Considerations</u>
			</p>
			<p class="privacy-policy__content">
				You have the right to receive notice of the categories of Personal Information we collect, and the purposes for which those categories of Personal Information will be used.
				The categories we use below to describe the information are those enumerated in the CCPA.
			</p>
			<p class="privacy-policy__content">
				California law requires us to provide information regarding the criteria we use to determine the length of time for which we retain personal information.
				We utilize the following criteria to determine the length of time for which we retain information:
			</p>
			<ol
				class="privacy-policy__list u-margin-top-small"
				aria-labelledby="CaliforniaNotices"
			>
				<li class="privacy-policy__list-item">
					The business purposes for which the information is used, and the length of time for which the information is required to achieve those purposes;
				</li>
				<li class="privacy-policy__list-item">
					Whether we are required to retain the information type in order to comply with legal obligations or contractual commitments, to defend against potential legal claims,
					or as otherwise necessary to investigate theft or other activities potentially in violation of our policies and procedures applicable to you or against the law,
					to ensure a secure online environment, or to protect health and safety;
				</li>
				<li class="privacy-policy__list-item">
					The privacy impact of ongoing retention on the consumer; and
				</li>
				<li class="privacy-policy__list-item">
					The manner in which information is maintained and flows through our systems, and how best to manage the lifecycle of information in light of the volume and complexity of the systems in our infrastructure.
				</li>
			</ol>
			<p class="privacy-policy__content">
				Individual pieces of personal information may exist in different systems that are used for different business or legal purposes.
				A different maximum retention period may apply to each use case of the information. Certain individual pieces of information may also be stored in combination with other individual pieces of information,
				and the maximum retention period may be determined by the purpose for which that information set is used.
			</p>
			<p class="privacy-policy__content">
				The following chart describes our practices with regard to the collection, use, and retention of your Personal Information.
				The categories we use to describe the information are those listed in the CCPA. Certain Personal information may fall into multiple categories, and in some circumstances,
				how we use and how long we keep the information within each category will vary.
				The examples of Personal Information listed below are illustrative and do not represent a complete description of the information we process.
			</p><br>
			<table class="privacy-policy__table">
				<tr class="privacy-policy__table-header">
					<th>Category</th>
					<th>Required Information</th>
				</tr>
				<tr>
					<td>Personal Identifiers</td>
					<td>
						<ul>
							<li>
								<u>Examples of what we collect</u>: Name, email address, phone number, and contact address, username and basic account information, unique identifiers (such as those assigned in cookies).
							</li>
							<li>
								<u>Sources</u>: Directly from you.
							</li>
							<li>
								<u>Purpose of collection and use</u>: All purposes listed in How We Use Your Information.
							</li>
							<li>
								<u>Retention Considerations</u>: We retain this Personal Information subject to the criteria set forth in Collection, Use and Retention Considerations above.
							</li>
						</ul>
					</td>
				</tr>
				<tr>
					<td>Personal information categories listed in the California Customer Records statute (Cal. Civ. Code § 1798.80(e))</td>
					<td>
						<ul>
							<li>
								<u>Examples of what we collect</u>: Name, email address, address, telephone number, bank account number, credit card number, debit card number, or any other financial information.
							</li>
							<li>
								<u>Sources</u>: Directly from you.
							</li>
							<li>
								<u>Purpose of collection and use</u>: All purposes listed in How We Use Your Information.
							</li>
							<li>
								<u>Retention Considerations</u>: We retain this Personal Information subject to the criteria set forth in Collection, Use and Retention Considerations above.
							</li>
						</ul>
					</td>
				</tr>
				<tr>
					<td>Protected classification characteristics under California or federal law</td>
					<td>Not collected.</td>
				</tr>
				<tr>
					<td>Commercial information</td>
					<td>
						<ul>
							<li>
								<u>Examples of what we collect</u>: Transaction information, billing records, payment records.
							</li>
							<li>
								<u>Sources</u>: Directly from you.
							</li>
							<li>
								<u>Purpose of collection and use</u>: All purposes listed in How We Use Your Information.
							</li>
							<li>
								<u>Retention Considerations</u>: We retain this Personal Information subject to the criteria set forth in Collection, Use and Retention Considerations above.
							</li>
						</ul>
					</td>
				</tr>
				<tr>
					<td>Biometric information</td>
					<td>Not collected.</td>
				</tr>
				<tr>
					<td>Internet or other similar network activity</td>
					<td>
						<ul>
							<li>
								<u>Examples of what we collect</u>: Unique numerical identifier, cookie or tracking pixel information, device ID, browsing history, search history, IP address,
								interaction with a website, or interaction with advertisement.
							</li>
							<li>
								<u>Sources</u>: Automatically from you.
							</li>
							<li>
								<u>Purpose of collection and use</u>: All purposes listed in How We Use Your Information.
							</li>
							<li>
								<u>Retention Considerations</u>: We retain this Personal Information subject to the criteria set forth in Collection, Use and Retention Considerations above.
							</li>
						</ul>
					</td>
				</tr>
				<tr>
					<td>Professional or employment related information</td>
					<td>Not collected.</td>
				</tr>
				<tr>
					<td>Education information</td>
					<td>Not collected.</td>
				</tr>
				<tr>
					<td>Audio, electronic, visual, thermal, olfactory, or similar information</td>
					<td>
						<ul>
							<li>
								<u>Examples of what we collect</u>: Call recordings (e.g., customer service calls).
							</li>
							<li>
								<u>Sources</u>: Directly from you.
							</li>
							<li>
								<u>Purpose of collection and use</u>: All purposes listed in How We Use Your Information.
							</li>
							<li>
								<u>Retention Considerations</u>: We retain this Personal Information subject to the criteria set forth in Collection, Use and Retention Considerations above.
							</li>
						</ul>
					</td>
				</tr>
				<tr>
					<td>Inferences drawn from other personal information</td>
					<td>Not collected.</td>
				</tr>
				<tr>
					<td>Sensitive information</td>
					<td>Not collected.</td>
				</tr>
			</table>
			<p class="privacy-policy__content">
				<u>Sale, Sharing, and Other Disclosure of Personal Information</u>
			</p>
			<p class="privacy-policy__content">
				Any category of Personal Information listed in the chart above may be disclosed for a business purpose to service providers and contractors, which are companies or individuals that we engage to conduct activities on our behalf.
				Service providers and contractors are restricted from using personal information for any purpose not related to our engagement.
			</p>
			<p class="privacy-policy__content">
				California law also requires us to provide you with information regarding the parties to whom we “sell” or “share” your information, and the parties to whom we disclose your information for a business purpose.
				Under the CCPA, a business “sells” Personal Information when it discloses Personal Information to a company for monetary or other benefit.
			</p>
			<p class="privacy-policy__content">
				We share your information as follows:
			</p>
			<ol
				class="privacy-policy__list u-margin-top-small"
				aria-labelledby="CaliforniaNotices"
			>
				<li class="privacy-policy__list-item">
					<u>Identifiers</u>: Analytics Partners; Advertising and Marketing Partners.
				</li>
				<li class="privacy-policy__list-item">
					<u>Personal information categories listed in the California Customer Records statute</u>: Analytics Partners; Advertising and Marketing Partners.
				</li>
				<li class="privacy-policy__list-item">
					<u>Internet or Electronic Network Activity Information</u>: Analytics Partners; Advertising and Marketing Partners.
				</li>
			</ol>
			<p class="privacy-policy__content">
				We may disclose your information in other ways that are not considered sales under the law. Please see "How We Share Personal Information" above for more information about how we share your information.
			</p>
			<p class="privacy-policy__content">
				<u>Right to Know and Request Access to and Deletion of Personal Information</u>
			</p>
			<p class="privacy-policy__content">
				You have the right to request access to Personal Information collected about you and information regarding the source of that information,
				the purposes for which we collect it, and the third parties and service providers with whom we share it.
				You also have the right to request we correct inaccurate Personal Information and to request, in certain circumstances, that we delete any Personal Information that we have collected directly from you.
			</p>
			<p class="privacy-policy__content">
				You may submit a request to exercise your rights to know/access or delete your Personal Information by sending an email to
				<epi-link
					to="mailto:compliance@embracepetinsurance.com"
					target="_blank"
				>compliance@embracepetinsurance.com</epi-link>, or by calling us at 1.800.511.9172.
				You may authorize another individual or a business registered with the California Secretary of State, called an authorized agent, to make requests on your behalf through these means.
			</p>
			<p class="privacy-policy__content">
				In order to process your request, we must verify your request.
				We do this by asking you to provide personal identifiers we can match against information we may have collected from you previously and confirm your request using the email account stated in the request.
			</p>
			<p class="privacy-policy__content">
				We may have a reason under the law why we do not have to comply with your request, or why we may comply with it in a more limited way than you anticipated.
				If we do, we will explain that to you in our response.
			</p>
			<p class="privacy-policy__content">
				<u>Right to Opt Out of Sale of Personal Information to Third Parties</u>
			</p>
			<p class="privacy-policy__content">
				You have the right to opt out of our sale of your Personal Information to third parties. To review our Notice of Right to Opt Out of Sale/Sharing and exercise your right,
				please email
				<epi-link
					to="mailto:compliance@embracepetinsurance.com"
					target="_blank"
				>compliance@embracepetinsurance.com</epi-link>.
				Please note that your right to opt out does not apply to our sharing of Personal Information with Service Providers, as described above.
			</p>
			<p class="privacy-policy__content">
				<u>Right to Limit Use of Sensitive Personal Information</u>
			</p>
			<p class="privacy-policy__content">
				You have the right to request that we limit use of your sensitive personal information to certain purposes allowed by law.
				However, we do not process your sensitive personal information in circumstances that would be subject to the right to limit.
			</p>
			<p class="privacy-policy__content">
				<u>Notice of Financial Incentive</u>
			</p>
			<p class="privacy-policy__content">
				We may offer rewards, discounts or other benefits to consumers enrolled in certain promotional or loyalty programs, such as an annual reward for participating in a particular plan or program.
				Consumers can opt-out of individual programs in their account settings (
				<epi-link
					to="https://my.embracepetinsurance.com"
					target="_blank"
				>https://my.embracepetinsurance.com</epi-link>) or by contacting us as indicated above.
				You may also submit a request that we delete your Personal Information as described in this Privacy Policy.
			</p>
			<p class="privacy-policy__content">
				Embrace does not generally assign monetary or other value to consumers' Personal Information, and our promotions activity changes continually.
				To the extent California law requires that a value be assigned to such programs, or the price or service differences they involve,
				Embrace values the Personal Information as being equal to the value of the discounts or other financial incentives provided in each such program.
				Such calculation is based upon a practical and good-faith effort to assess, on an aggregate basis for all collected information:
				(1) the data elements collected (e.g., email address),
				(2) the use of such information by Embrace in connection with its marketing activities,
				(3) the range of discounts provided (which can depend on each consumer's purchases under such offers),
				(4) the number of consumers enrolled in respective programs, and
				(5) the product or service to which the benefits (such as price difference) applies.
				These values can change over time.
			</p>
		</section>
		<section id="privacy-policyVirginiaColoradoConnecticutUtah">
			<p class="privacy-policy__content m--bold">
				VIRGINIA, COLORADO, CONNECTICUT, AND UTAH
			</p>
			<p class="privacy-policy__content">
				Laws in Virginia, Colorado, Connecticut, and Utah provide residents of these states with rights to access, delete, and correct certain “Personal Data” we collect about them,
				as well as to restrict the use of that Personal Data for targeted advertising, restrict the “sale” of that Personal Data, and control our use of Personal Data considered sensitive.
				If you are a resident of any of these states, you also have a right not to receive discriminatory treatment for the exercise of your privacy rights.
			</p>
		</section>
	</section>
	<section
		id="privacy-policyContactWithPhone"
		class="privacy-policy__listgroup u-margin-top-small"
	>
		<p class="privacy-policy__content m--bold">
			Contact Information
		</p>
		<p class="privacy-policy__content">
			To ask questions or comment about this privacy policy and our privacy practices, contact us at:
		</p>
		<ul>
			<li>Embrace Pet Insurance</li>
			<li>P.O. Box 22188 </li>
			<li>Beachwood, OH 44122-0188 </li>
			<li>or by telephone at: 800-511-9172</li>
			<li>or by email at:
				<epi-link
					to="mailto:compliance@embracepetinsurance.com"
					target="_blank"
				>compliance@embracepetinsurance.com</epi-link>
			</li>
		</ul>
	</section>
</mat-dialog-content>
